import React, { useCallback, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { Biotech, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { examApplicationService } from '../../../services/examApplicationService';
import { examConvocationService } from '../../../services/examConvocationService';
import type { ExamWithApplication } from '../../../types/examApplication';
import {
  ExpirationStatus,
  ExpirationStatusLabels,
  ExpirationStatusColors,
  formatDaysUntilExpiration,
  ApplicationLevelLabels,
} from '../../../types/examApplication';
import type { ConvocationItem, ConvocationStatus } from '../../../types/examConvocation';
import { NIVEIS_DE_APLICACAO } from '../../../types/nivelDeAplicacao';

interface EmployeeExamsCardProps {
  employeeId: number;
  companyId: number;
  departmentId: number;
  occupationId: number;
  /** A unidade do setor do funcionário: é por ela que o aplicado à unidade chega a ele (#541). */
  unidadeDoSetorId?: number | string | null;
}

const ACCENT = '#1565C0';
const PAGE_SIZE = 5;
/** O teto da rota da Convocação; um funcionário não tem mais exames que isso. */
const LIMITE_DA_CONVOCACAO = 100;

const SEM_VENCIMENTO = 'SEM_VENCIMENTO';
type Situacao = ExpirationStatus | typeof SEM_VENCIMENTO;

const SITUACAO_DA_CONVOCACAO: Record<ConvocationStatus, ExpirationStatus> = {
  overdue: ExpirationStatus.EXPIRED,
  expiring: ExpirationStatus.EXPIRING_SOON,
  ok: ExpirationStatus.OK,
};

// Vencido primeiro, depois o que vence logo: é a ordem em que se age sobre a lista.
const URGENCIA: Record<Situacao, number> = {
  [ExpirationStatus.EXPIRED]: 0,
  [ExpirationStatus.EXPIRING_SOON]: 1,
  [ExpirationStatus.OK]: 2,
  [SEM_VENCIMENTO]: 3,
};

interface Selo {
  situacao: Situacao;
  rotulo: string;
  cor: 'success' | 'warning' | 'error' | 'default';
}

interface VencimentoDoExame {
  selo: Selo;
  /** A linha da lista que leva o selo: um exame aplicado em dois níveis ganha um selo só. */
  chaveDaLinha: string;
}

function chaveDoExame(examId: number | string): string {
  return String(Number(examId));
}

function chaveDaLinha(ex: ExamWithApplication): string {
  return `${ex.applicationLevel}-${ex.id}`;
}

/**
 * Sem linha na Convocação (exame não obrigatório ou sem periodicidade) ou sem próxima data
 * (funcionário sem admissão e sem exame) não há conta possível — e isso não é "Em dia".
 */
function seloDa(linha: ConvocationItem | undefined): Selo {
  if (!linha || !linha.nextDueDate) {
    return { situacao: SEM_VENCIMENTO, rotulo: 'Sem vencimento', cor: 'default' };
  }
  const situacao = SITUACAO_DA_CONVOCACAO[linha.status];
  const rotulo =
    situacao === ExpirationStatus.OK
      ? ExpirationStatusLabels[situacao]
      : formatDaysUntilExpiration(-linha.daysOverdue);
  return { situacao, rotulo, cor: ExpirationStatusColors[situacao] };
}

function maisEspecifica(linhas: ExamWithApplication[]): ExamWithApplication {
  return linhas.reduce((atual, candidata) =>
    NIVEIS_DE_APLICACAO.indexOf(candidata.applicationLevel) > NIVEIS_DE_APLICACAO.indexOf(atual.applicationLevel)
      ? candidata
      : atual
  );
}

/** Um vencimento por exame, casado por `examId` com a linha da Convocação do funcionário. */
function vencimentosPorExame(
  exams: readonly ExamWithApplication[],
  convocacao: readonly ConvocationItem[]
): Map<string, VencimentoDoExame> {
  const linhaPorExame = new Map(convocacao.map((linha) => [chaveDoExame(linha.examId), linha]));
  const linhasPorExame = new Map<string, ExamWithApplication[]>();
  for (const ex of exams) {
    const chave = chaveDoExame(ex.examId);
    linhasPorExame.set(chave, [...(linhasPorExame.get(chave) ?? []), ex]);
  }

  const vencimentos = new Map<string, VencimentoDoExame>();
  for (const [chave, linhas] of linhasPorExame) {
    const linhaDaConvocacao = linhaPorExame.get(chave);
    const doNivelQueVale = linhas.find((ex) => ex.applicationLevel === linhaDaConvocacao?.applicationLevel);
    vencimentos.set(chave, {
      selo: seloDa(linhaDaConvocacao),
      chaveDaLinha: chaveDaLinha(doNivelQueVale ?? maisEspecifica(linhas)),
    });
  }
  return vencimentos;
}

export const EmployeeExamsCard: React.FC<EmployeeExamsCardProps> = ({
  employeeId,
  companyId,
  departmentId,
  occupationId,
  unidadeDoSetorId = null,
}) => {
  const [page, setPage] = useState(0);

  // Aplicação de exame tem permissão própria: os recortes vinham com `catch` vazio, e
  // o cartão ficava vazio e mudo para quem não lê aplicação de exame.
  const carregar = useCallback(async () => {
    const [comp, unid, dept, occ, emp] = await Promise.all([
      examApplicationService.getForCompany(companyId),
      unidadeDoSetorId == null
        ? Promise.resolve({ applied: [] })
        : examApplicationService.getForUnit(Number(unidadeDoSetorId)),
      examApplicationService.getForDepartment(departmentId),
      examApplicationService.getForOccupation(occupationId),
      examApplicationService.getForEmployee(employeeId),
    ]);
    return [
      ...(comp.applied ?? []),
      ...(unid.applied ?? []),
      ...(dept.applied ?? []),
      ...(occ.applied ?? []),
      ...(emp.applied ?? []),
    ];
  }, [companyId, unidadeDoSetorId, departmentId, occupationId, employeeId]);

  const lista = useListaPermitida<ExamWithApplication>(
    'exam_application.read',
    'Exames aplicados',
    carregar
  );
  const aplicados = lista.itens;
  const loading = lista.carregando;

  // O vencimento é por funcionário e só se vê com a permissão da Convocação; sem ela o
  // cartão lista os exames sem chip nenhum, em vez de inventar "Em dia".
  const carregarVencimentos = useCallback(async () => {
    const resposta = await examConvocationService.getConvocations({
      companyId,
      employeeId,
      limit: LIMITE_DA_CONVOCACAO,
    });
    return resposta.items;
  }, [companyId, employeeId]);

  const convocacao = useListaPermitida<ConvocationItem>(
    'exam_summons.read',
    'Vencimento dos exames',
    carregarVencimentos
  );
  const mostraVencimento = convocacao.permitido && convocacao.resolvida && !convocacao.erro;

  const vencimentos = useMemo(
    () => (mostraVencimento ? vencimentosPorExame(aplicados, convocacao.itens) : null),
    [mostraVencimento, aplicados, convocacao.itens]
  );

  const exams = useMemo(() => {
    if (!vencimentos) return aplicados;
    const urgenciaDe = (ex: ExamWithApplication) =>
      URGENCIA[vencimentos.get(chaveDoExame(ex.examId))?.selo.situacao ?? SEM_VENCIMENTO];
    return [...aplicados].sort((um, outro) => urgenciaDe(um) - urgenciaDe(outro));
  }, [aplicados, vencimentos]);

  const countByStatus = useMemo(() => {
    const contagem: Partial<Record<Situacao, number>> = {};
    for (const { selo } of vencimentos?.values() ?? []) {
      contagem[selo.situacao] = (contagem[selo.situacao] ?? 0) + 1;
    }
    return contagem;
  }, [vencimentos]);

  const totalPages = Math.ceil(exams.length / PAGE_SIZE);
  const paged = exams.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><Biotech fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Exames Ocupacionais
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={exams.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{lista.erro}</Alert>
        )}

        {!loading && !lista.erro && exams.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && exams.length > 0 && (
          <>
            {/* Summary chips */}
            {vencimentos && (
              <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
                {([ExpirationStatus.OK, ExpirationStatus.EXPIRING_SOON, ExpirationStatus.EXPIRED] as const).map((s) =>
                  countByStatus[s] ? (
                    <Chip
                      key={s}
                      label={`${ExpirationStatusLabels[s]}: ${countByStatus[s]}`}
                      size="small"
                      color={ExpirationStatusColors[s]}
                      variant="outlined"
                    />
                  ) : null
                )}
              </Box>
            )}

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((ex) => {
                const vencimento = vencimentos?.get(chaveDoExame(ex.examId));
                const selo = vencimento?.chaveDaLinha === chaveDaLinha(ex) ? vencimento.selo : null;
                return (
                  <Box
                    key={chaveDaLinha(ex)}
                    data-testid="exame-da-ficha"
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Chip
                      label={ApplicationLevelLabels[ex.applicationLevel]}
                      size="small"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', minWidth: 56 }}
                    />
                    <Typography variant="caption" sx={{ flex: 1 }} noWrap title={ex.examName}>
                      {ex.examName}
                    </Typography>
                    {selo && (
                      <Chip
                        label={selo.rotulo}
                        size="small"
                        color={selo.cor}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, exams.length)} de {exams.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
